import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ConfigService } from '@core/services/config';
import { LoadingService } from '@core/services/loading.service';
import { Store } from '@ngxs/store';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { ButtonDirective } from '@shared/directives/button.directive';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { TruncatePipe } from '@shared/pipes/truncate.pipe';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { BehaviorSubject, filter, forkJoin, Observable, ObservableInput, Subscription, take } from 'rxjs';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { IFILTER } from 'app/app.constants';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    InputComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonDirective,
    PermissionDirective,
    TruncatePipe,
    FiltersComponent,
    TableNavigatorComponent,
    TableRefreshComponent
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit, OnDestroy {
  fb = inject(FormBuilder);
  store = inject(Store);
  breadcrumbService = inject(BreadCrumbService);
  activatedRoute = inject(ActivatedRoute);
  loadingService = inject(LoadingService);
  configService = inject(ConfigService);
  toast = inject(NotificationFacade);
  router = inject(Router);

  configListSubject = new BehaviorSubject<any>(null);
  totalCountSubject = new BehaviorSubject<number>(0);
  configList$ = this.configListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  isFetchingConfigList$!: Observable<boolean>;
  isUpdating$!: Observable<boolean>;

  // Track which row is in edit mode
  editingKey: string | null = null;

  // One FormControl per config
  rowForms: Record<string, FormControl> = {};
  latestConfigMap: Record<string, any> = {};
  subs: Subscription[] = [];

  isFilterActive = false;
  hadFilterBefore = false;

  filterStructure: IFILTER[] = [
    {
      filterKey: 'search',
      label: 'Search Settings',
      type: 'text',
    }
  ];

  currentPage = 1;

  constructor() {
    this.configList$ = this.store.select(
      TableSelectors.getTableData('configurations')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('configurations')
    );
    this.isFetchingConfigList$ = this.store.select(
      TableSelectors.getTableLoading('configurations')
    );
    this.isUpdating$ = this.loadingService.getLoadingObservable('update-configuration');
  }

  ngOnInit(): void {
    this.breadcrumbService.setPageBreadCrumb(this.activatedRoute)
    this.loadConfigList();
    this.listenAndBuildRowForms();
    this.updateLatestConfigMap();
    this.configFiltersEventListener();
  }

  configFiltersEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';

      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.isFilterActive = true;
        this.hadFilterBefore = true; // filters are currently active

        this.store.dispatch(new TableActions.SetTableFilters({
          tableKey: 'configurations',
          filters: [{ key: 'search', value: params['search'] }]
        }));

        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'configurations',
            params: new Pagination({ skip: skip, size: take }),
          })
        );
      } else {
        this.isFilterActive = false;
  
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          
          this.store.dispatch(new TableActions.SetTableFilters({
            tableKey: 'configurations',
            filters: [{ key: 'search', value: '' }]
          }));

          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'configurations',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  loadConfigList(page = 1) {
    const sub = this.configList$.pipe(take(1)).subscribe((config) => {
      const hasData = Array.isArray(config) && config.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'configurations',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  listenAndBuildRowForms() {
    const sub = this.configList$
      .pipe(filter(Boolean)) // ensures data exists
      .subscribe(configs => {
        configs.forEach((config: any) => {
          if (!this.rowForms[config.key]) {
            this.rowForms[config.key] = new FormControl(
              config.value ?? '',
              Validators.required
            );
          } else {
            // Update existing control with new value if not currently editing
            if (this.editingKey !== config.key) {
              this.rowForms[config.key].setValue(config.value ?? '', { emitEvent: false });
            }
          }
        });
      });

    this.subs.push(sub);
  }

  updateLatestConfigMap() {
    const sub = this.configList$
      .pipe(filter(Boolean))
      .subscribe(configs => {
        this.latestConfigMap = configs.reduce((acc: any, config: any) => {
          acc[config.key] = config.value;
          return acc;
        }, {});
      });

    this.subs.push(sub);
  }

  enableEdit(key: string) {
    this.editingKey = key;
  }

  // Close the currently editing row
  closeEdit() {
    if (this.editingKey) {
      this.cancelEdit(this.editingKey);
    }
  }

  cancelEdit(key: string) {
    const control = this.rowForms[key];
    if (!control) return;

    // Restore value from the latest config map
    const currentValue = this.latestConfigMap[key] ?? '';

    control.setValue(currentValue, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();

    this.editingKey = null;
  }

  saveRow(key: string) {
    const control = this.rowForms[key];
    if (control.invalid) return;

    const payload = { key, value: String(control.value) };

    const sub = this.configService.updateConfig(payload).subscribe({
      next: () => {
        this.toast.success('Configuration updated successfully');
        this.editingKey = null;

        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'configurations',
            params: new Pagination({ skip: 0, size: 10 }),
          })
        );
      },
      error: () => {
        this.toast.error('Failed to update configuration');
      }
    });

    this.subs.push(sub);
  }

  navigate(page: number) {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...params, page: page },
        queryParamsHandling: 'merge'
      });
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
